<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete', 'statusChange']);

const [showActionsMenu, toggleActionsMenu] = useToggle(false);
const [showStatusMenu, toggleStatusMenu] = useToggle(false);
const [expanded, toggleExpanded] = useToggle(false);

const actionMenuItems = computed(() => [
  { label: 'Edit', action: 'edit', value: 'edit', icon: 'i-lucide-pencil' },
  {
    label: 'Delete',
    action: 'delete',
    value: 'delete',
    icon: 'i-lucide-trash-2',
  },
]);

const statusMenuItems = computed(() => [
  {
    label: 'Pendiente',
    action: 'status',
    value: 'Pendiente',
    icon: 'i-lucide-circle-pause',
  },
  {
    label: 'En Proceso',
    action: 'status',
    value: 'En Proceso',
    icon: 'i-lucide-loader-circle',
  },
  {
    label: 'Completada',
    action: 'status',
    value: 'Completada',
    icon: 'i-lucide-circle-check',
  },
]);

const statusColor = computed(() => {
  if (props.task.isCompleted) return 'text-green-600 dark:text-green-400';
  if (props.task.status === 'En Proceso')
    return 'text-blue-600 dark:text-blue-400';
  return 'text-slate-600 dark:text-slate-400';
});

const statusIcon = computed(() => {
  if (props.task.isCompleted) return 'i-lucide-circle-check';
  if (props.task.status === 'En Proceso') return 'i-lucide-loader-circle';
  return 'i-lucide-circle-pause';
});

const statusBadgeClass = computed(() => {
  if (props.task.isCompleted)
    return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
  if (props.task.status === 'En Proceso')
    return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
  return 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-400';
});

const isOverdue = computed(() => {
  if (props.task.isCompleted) return false;
  if (!props.task.dueDate) return false;
  const dueDate = new Date(props.task.dueDate);
  return dueDate < new Date();
});

const hasDescription = computed(
  () =>
    props.task.description &&
    props.task.description !== 'Sin descripción' &&
    props.task.description.trim().length > 0
);

const handleActionClick = ({ action }) => {
  toggleActionsMenu(false);
  if (action === 'edit') {
    emit('edit', props.task);
  } else if (action === 'delete') {
    emit('delete', props.task);
  }
};

const handleStatusClick = ({ value }) => {
  toggleStatusMenu(false);
  emit('statusChange', props.task, value);
};
</script>

<template>
  <div
    class="p-3 rounded-lg border transition-colors"
    :class="[
      task.isCompleted
        ? 'bg-n-slate-2 dark:bg-n-slate-11 border-n-slate-4 dark:border-n-slate-9'
        : 'bg-white dark:bg-n-slate-12 border-n-slate-4 dark:border-n-slate-9 hover:border-n-slate-6 dark:hover:border-n-slate-7',
    ]"
  >
    <!-- Header -->
    <div class="flex items-start justify-between gap-2 mb-2">
      <div class="flex items-start gap-2 flex-1 min-w-0">
        <Icon
          :icon="statusIcon"
          :class="statusColor"
          size="16"
          class="mt-0.5 flex-shrink-0"
        />
        <h4
          class="text-sm font-medium line-clamp-2 cursor-pointer"
          :class="[
            task.isCompleted
              ? 'line-through text-n-slate-11 dark:text-n-slate-8'
              : 'text-n-slate-12 dark:text-n-slate-1',
          ]"
          @click="toggleExpanded()"
        >
          {{ task.title }}
        </h4>
      </div>

      <!-- Actions menu -->
      <div
        v-on-clickaway="() => toggleActionsMenu(false)"
        class="relative flex-shrink-0"
      >
        <button
          class="p-1 rounded hover:bg-n-slate-3 dark:hover:bg-n-slate-10 text-n-slate-11"
          @click="toggleActionsMenu()"
        >
          <Icon icon="i-lucide-more-vertical" size="16" />
        </button>
        <DropdownMenu
          v-if="showActionsMenu"
          :menu-items="actionMenuItems"
          class="mt-1 ltr:right-0 rtl:left-0 top-full"
          @action="handleActionClick"
        />
      </div>
    </div>

    <!-- Description (expandable) -->
    <div v-if="hasDescription" class="mb-2">
      <p
        class="text-xs"
        :class="[
          expanded ? '' : 'line-clamp-2',
          task.isCompleted
            ? 'text-n-slate-10 dark:text-n-slate-9'
            : 'text-n-slate-11 dark:text-n-slate-10',
        ]"
      >
        {{ task.description }}
      </p>
      <button
        v-if="task.description.length > 80"
        class="text-xs text-n-slate-9 hover:text-n-slate-11 mt-1"
        @click="toggleExpanded()"
      >
        {{ expanded ? $t('TASKS.SHOW_LESS') : $t('TASKS.SHOW_MORE') }}
      </button>
    </div>

    <!-- Meta info row 1: dates -->
    <div class="flex items-center gap-3 text-xs mb-1.5">
      <!-- Due date -->
      <div
        v-if="task.dueDate"
        class="flex items-center gap-1"
        :class="[
          isOverdue
            ? 'text-red-600 dark:text-red-400 font-medium'
            : 'text-n-slate-11 dark:text-n-slate-10',
        ]"
      >
        <Icon icon="i-lucide-calendar" size="12" />
        <span>{{ task.dueDate }}</span>
        <span v-if="isOverdue" class="text-red-500 font-semibold">
          {{ $t('TASKS.OVERDUE_WRAPPED') }}
        </span>
      </div>
    </div>

    <!-- Meta info row 2: people -->
    <div class="flex items-center gap-3 text-xs mb-1.5">
      <!-- Assigned to -->
      <div
        v-if="task.assignedTo"
        class="flex items-center gap-1 text-n-slate-11 dark:text-n-slate-10"
      >
        <Icon icon="i-lucide-user-cog" size="12" />
        <span>{{ task.assignedTo }}</span>
      </div>

      <!-- Created by -->
      <div
        v-if="task.createdBy"
        class="flex items-center gap-1 text-n-slate-10 dark:text-n-slate-9"
      >
        <Icon icon="i-lucide-user-plus" size="12" />
        <span>{{ task.createdBy }}</span>
      </div>
    </div>

    <!-- Meta info row 3: task ID + created date -->
    <div class="flex items-center justify-between gap-2 text-xs">
      <div class="flex items-center gap-3 text-n-slate-10 dark:text-n-slate-9">
        <!-- Task ID -->
        <div class="flex items-center gap-1">
          <Icon icon="i-lucide-hash" size="12" />
          <span>{{ task.messageId }}</span>
        </div>

        <!-- Created date -->
        <div v-if="task.createdAtFormatted" class="flex items-center gap-1">
          <Icon icon="i-lucide-clock" size="12" />
          <span>{{ task.createdAtFormatted }}</span>
        </div>
      </div>

      <!-- Status selector -->
      <div v-on-clickaway="() => toggleStatusMenu(false)" class="relative">
        <button
          class="px-2 py-1 text-xs font-medium rounded-full transition-colors"
          :class="statusBadgeClass"
          @click="toggleStatusMenu()"
        >
          {{ task.status }}
        </button>
        <DropdownMenu
          v-if="showStatusMenu"
          :menu-items="statusMenuItems"
          class="mt-1 ltr:right-0 rtl:left-0 top-full"
          @action="handleStatusClick"
        />
      </div>
    </div>
  </div>
</template>
